import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import '../styles/tip.css';

/**
 * Tooltip do produto, no lugar do `title` do navegador.
 *
 * ── Por que trocar ──
 * O `title` nativo demora ~500 ms para aparecer, tem a aparencia do sistema
 * operacional (que muda entre Windows, macOS e Linux) e nao aceita estilo
 * nenhum. Numa tela em que o significado dos icones depende do hover, meio
 * segundo de espera e a diferenca entre a explicacao existir e nao existir.
 *
 * ── Por que um so no DOM, via portal ──
 * A lista de conversas rola dentro de um container com `overflow-y: auto`. Um
 * tooltip renderizado como filho da linha seria CORTADO na borda do container —
 * exatamente nas linhas de cima e de baixo, que sao as que mais precisam. O
 * portal no `body` com `position: fixed` escapa de qualquer overflow.
 *
 * ── Por que hook e nao componente que embrulha ──
 * `<Tip><span/></Tip>` acrescentaria um no ao DOM em toda linha, e um wrapper
 * com `display` proprio mudaria layouts que ja estao ajustados. `useTip()`
 * devolve PROPS para espalhar no elemento que ja existe — zero no novo, zero
 * mudanca de layout.
 */

interface Alvo {
  readonly texto: string;
  readonly x: number;
  readonly y: number;
  /** Quanto a caixa andou para caber na janela; a seta volta isso para apontar o elemento. */
  readonly seta: number;
  /** Abaixo quando nao ha espaco em cima — perto do topo da janela. */
  readonly abaixo: boolean;
}

type Mostrar = (texto: string, alvo: HTMLElement | null) => void;

const TipContext = createContext<Mostrar | null>(null);

/** Curto o bastante para nao parecer travado, longo o bastante para nao piscar. */
const ATRASO_MS = 140;

const ID = 'zenda-tip';

/** O `max-width` de `.tooltip` em tip.css. */
const LARGURA_MAXIMA = 260;

export function TipProvider({ children }: { readonly children: ReactNode }): ReactElement {
  const [alvo, setAlvo] = useState<Alvo | null>(null);
  const timer = useRef<number | undefined>(undefined);

  const mostrar = useCallback<Mostrar>((texto, elemento) => {
    window.clearTimeout(timer.current);

    if (texto === '' || elemento === null) {
      setAlvo(null);
      return;
    }

    timer.current = window.setTimeout(() => {
      const r = elemento.getBoundingClientRect();
      // Se o elemento saiu da tela enquanto o timer corria (rolagem, troca de
      // filtro), nao ha o que apontar.
      if (r.width === 0 && r.height === 0) return;

      const abaixo = r.top < 56;
      // Centralizado num elemento colado à borda, metade da caixa sairia da janela.
      const meia = LARGURA_MAXIMA / 2 + 8;
      const centro = r.left + r.width / 2;
      const x = Math.min(Math.max(centro, meia), window.innerWidth - meia);
      setAlvo({
        texto,
        x,
        seta: centro - x,
        y: abaixo ? r.bottom + 8 : r.top - 8,
        abaixo,
      });
    }, ATRASO_MS);
  }, []);

  // Rolar com o tooltip aberto o deixaria apontando para o nada.
  useEffect(() => {
    if (alvo === null) return;
    const fechar = (): void => setAlvo(null);
    window.addEventListener('scroll', fechar, true);
    window.addEventListener('wheel', fechar, { passive: true });
    return () => {
      window.removeEventListener('scroll', fechar, true);
      window.removeEventListener('wheel', fechar);
    };
  }, [alvo]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <TipContext.Provider value={mostrar}>
      {children}
      {alvo === null
        ? null
        : createPortal(
            <div
              id={ID}
              role="tooltip"
              className={`tooltip${alvo.abaixo ? ' tooltip--abaixo' : ''}`}
              style={{ left: alvo.x, top: alvo.y, '--seta': `${alvo.seta}px` } as CSSProperties}
            >
              {alvo.texto}
            </div>,
            document.body,
          )}
    </TipContext.Provider>
  );
}

interface TipProps {
  readonly onMouseEnter: (e: { currentTarget: HTMLElement }) => void;
  readonly onMouseLeave: () => void;
  readonly onFocus: (e: { currentTarget: HTMLElement }) => void;
  readonly onBlur: () => void;
  readonly 'aria-describedby': string;
}

/**
 * `const tip = useTip();` e depois `<span {...tip('explicacao')} />`.
 *
 * `onFocus`/`onBlur` junto com o mouse porque quem navega por teclado precisa
 * da mesma explicacao — e era outra coisa que o `title` dava de graca e que
 * seria perdida numa troca descuidada.
 */
export function useTip(): (texto: string) => TipProps {
  const mostrar = useContext(TipContext);
  if (mostrar === null) throw new Error('useTip fora de <TipProvider>');

  return useMemo(
    () => (texto: string) => ({
      onMouseEnter: (e: { currentTarget: HTMLElement }) => mostrar(texto, e.currentTarget),
      onMouseLeave: () => mostrar('', null),
      onFocus: (e: { currentTarget: HTMLElement }) => mostrar(texto, e.currentTarget),
      onBlur: () => mostrar('', null),
      'aria-describedby': ID,
    }),
    [mostrar],
  );
}
